import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useLazyGetAllClinicUsersQuery,
  useSuspendClinicUserAccountMutation,
  useFreezeClinicUserAccountMutation,
  useDisableClinicUserAccountMutation,
  useActivateClinicUserAccountMutation,
  useDeleteClinicUserMutation,
} from "../../store/userAccountApi";
import useAuth from "../../hooks/useAuth";

function getErrorMessage(error, fallback) {
  if (error?.data?.message) return error.data.message;
  if (error?.message) {
    if (error.message.includes("Network Error"))
      return "Bağlantı hatası. İnternet bağlantınızı kontrol edin.";
    if (error.message.includes("401"))
      return "Oturum süreniz dolmuş. Tekrar giriş yapın.";
    if (error.message.includes("403"))
      return "Bu işlem için yetkiniz bulunmamaktadır.";
  }
  return fallback;
}

export default function useStaffManagement() {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [getAllUsers, { data: usersData, isLoading: isLoadingUsers, error: usersError }] =
    useLazyGetAllClinicUsersQuery();
  const [suspendAccount, { isLoading: isSuspending }] = useSuspendClinicUserAccountMutation();
  const [freezeAccount, { isLoading: isFreezing }] = useFreezeClinicUserAccountMutation();
  const [disableAccount, { isLoading: isDisabling }] = useDisableClinicUserAccountMutation();
  const [activateAccount, { isLoading: isActivating }] = useActivateClinicUserAccountMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteClinicUserMutation();

  const isActionLoading = isSuspending || isFreezing || isDisabling || isActivating;

  const [users, setUsers] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [reason, setReason] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(() => {
    setSnackbar((p) => ({ ...p, open: false }));
  }, []);

  useEffect(() => {
    if (usersData?.data?.users) {
      setUsers(
        usersData.data.users.map((u) => ({
          id: u._id,
          name: u.name,
          surname: u.surname || u.lastName,
          email: u.email,
          phone: u.tellNo || u.tel || u.phone,
          role: u.role,
          roleTemplate: u.roleTemplate || null,
          title: u.title || null,
          accountStatus: u.accountStatus || "Aktif",
          statusReason: u.statusReason,
          suspensionEndDate: u.suspensionEndDate,
          freezeEndDate: u.freezeEndDate,
          tcNo: u.tcNo,
          createdAt: u.createdInfo || u.createdAt,
          lastLogin: u.lastLogin,
          clinic: u.clinic
            ? {
                id: u.clinic._id,
                name: u.clinic.name,
                status: u.clinic.status,
                logoUrl: u.clinic.logoUrl || null,
                logoIconUrl: u.clinic.logoIconUrl || null,
              }
            : null,
        }))
      );
    }
  }, [usersData]);

  // Derin bağlantı: dashboard'daki personel satırından `?userId=...` ile
  // gelindiğinde ilgili personelin detay drawer'ı açılır. Parametre bir kez
  // tüketilip URL'den düşürülür — drawer kapatılınca yeniden açılmasın.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkUserId = searchParams.get("userId");
  const deepLinkConsumed = useRef(false);

  useEffect(() => {
    if (!deepLinkUserId || deepLinkConsumed.current || users.length === 0) return;
    const target = users.find((u) => u.id === deepLinkUserId);
    deepLinkConsumed.current = true;
    if (target) {
      setSelectedUser(target);
      setDetailDrawerOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [deepLinkUserId, users, setSearchParams]);

  const refreshUsers = useCallback(() => {
    if (token)
      getAllUsers({ token })
        .unwrap()
        .catch((e) => showSnackbar(getErrorMessage(e, "Personel listesi yüklenirken hata oluştu!"), "error"));
  }, [token, getAllUsers, showSnackbar]);

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const counts = useMemo(
    () => ({
      "": users.length,
      Aktif: users.filter((u) => u.accountStatus === "Aktif").length,
      "Askıya Alındı": users.filter((u) => u.accountStatus === "Askıya Alındı").length,
      Donduruldu: users.filter((u) => u.accountStatus === "Donduruldu").length,
      "Devre Dışı": users.filter((u) => u.accountStatus === "Devre Dışı").length,
    }),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const nameText = searchName.trim().toLowerCase();
    const emailText = searchEmail.trim().toLowerCase();
    const filtered = users.filter((u) => {
      if (
        nameText &&
        !(u.name || "").toLowerCase().includes(nameText) &&
        !(u.surname || "").toLowerCase().includes(nameText)
      )
        return false;
      if (emailText && !(u.email || "").toLowerCase().includes(emailText)) return false;
      if (statusFilter && u.accountStatus !== statusFilter) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [users, searchName, searchEmail, statusFilter]);

  const pagedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const openMenu = useCallback((e, user) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuUser(user);
  }, []);
  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuUser(null);
  }, []);

  const openDetail = useCallback(() => {
    setSelectedUser(menuUser);
    setDetailDrawerOpen(true);
    closeMenu();
  }, [menuUser, closeMenu]);

  const openActionDialog = useCallback(
    (type) => {
      setSelectedUser(menuUser);
      setDialogType(type);
      setDialogOpen(true);
      setReason("");
      setEndDate(null);
      closeMenu();
    },
    [menuUser, closeMenu]
  );

  const closeActionDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedUser(null);
    setDialogType("");
    setReason("");
    setEndDate(null);
  }, []);

  const handleAccountAction = useCallback(async () => {
    if (!selectedUser || !token) return;
    try {
      const payload = {
        token,
        id: selectedUser.id,
        reason: reason.trim(),
        ...(endDate && { endDate: endDate.toISOString() }),
      };
      switch (dialogType) {
        case "suspend":
          if (!reason.trim()) {
            showSnackbar("Askıya alma sebebi zorunludur", "error");
            return;
          }
          await suspendAccount(payload).unwrap();
          showSnackbar("Personel hesabı askıya alındı");
          break;
        case "freeze":
          if (!reason.trim()) {
            showSnackbar("Dondurma sebebi zorunludur", "error");
            return;
          }
          await freezeAccount(payload).unwrap();
          showSnackbar("Personel hesabı donduruldu");
          break;
        case "disable":
          if (!reason.trim()) {
            showSnackbar("Devre dışı bırakma sebebi zorunludur", "error");
            return;
          }
          await disableAccount({ token, id: selectedUser.id, reason: reason.trim() }).unwrap();
          showSnackbar("Personel hesabı devre dışı bırakıldı");
          break;
        case "activate":
          await activateAccount({ token, id: selectedUser.id }).unwrap();
          showSnackbar("Personel hesabı aktifleştirildi");
          break;
        default:
          return;
      }
      refreshUsers();
      closeActionDialog();
    } catch (err) {
      showSnackbar(getErrorMessage(err, "İşlem sırasında bir hata oluştu"), "error");
    }
  }, [
    selectedUser,
    token,
    dialogType,
    reason,
    endDate,
    suspendAccount,
    freezeAccount,
    disableAccount,
    activateAccount,
    refreshUsers,
    closeActionDialog,
    showSnackbar,
  ]);

  const openDeleteDialog = useCallback(() => {
    setDeleteTargetUser(menuUser);
    setDeleteDialogOpen(true);
    closeMenu();
  }, [menuUser, closeMenu]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTargetUser || !token) return;
    try {
      await deleteUser({ token, id: deleteTargetUser.id }).unwrap();
      showSnackbar("Personel başarıyla silindi.");
      setDeleteDialogOpen(false);
      setDeleteTargetUser(null);
      refreshUsers();
    } catch (err) {
      showSnackbar(getErrorMessage(err, "Personel silinirken bir hata oluştu"), "error");
    }
  }, [deleteTargetUser, token, deleteUser, refreshUsers, showSnackbar]);

  return {
    token,
    isLoadingUsers,
    usersError,
    users,
    filteredUsers,
    pagedUsers,
    counts,
    refreshUsers,

    searchName,
    setSearchName,
    searchEmail,
    setSearchEmail,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,

    menuAnchorEl,
    menuUser,
    openMenu,
    closeMenu,
    openDetail,
    detailDrawerOpen,
    setDetailDrawerOpen,
    selectedUser,

    dialogOpen,
    dialogType,
    reason,
    setReason,
    endDate,
    setEndDate,
    openActionDialog,
    closeActionDialog,
    handleAccountAction,
    isActionLoading,

    deleteDialogOpen,
    setDeleteDialogOpen,
    deleteTargetUser,
    setDeleteTargetUser,
    openDeleteDialog,
    handleDeleteConfirm,
    isDeleting,

    snackbar,
    showSnackbar,
    closeSnackbar,
  };
}
